import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Grid,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  PlayCircle as PlayCircleIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from 'axios';

const AuditTrail = ({ entityType, entityId, entityName, onClose }) => {
  const [auditData, setAuditData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  useEffect(() => {
    fetchAuditTrail();
  }, [entityType, entityId]);

  const fetchAuditTrail = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      let endpoint = '';
      if (entityType === 'charter') {
        endpoint = `/api/audit/charter/${entityId}`;
      } else if (entityType === 'sector') {
        endpoint = `/api/audit/sector/${entityId}`;
      } else {
        throw new Error('Invalid entity type');
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAuditData(response.data);
    } catch (error) {
      console.error('Failed to fetch audit trail:', error);
      setError(error.response?.data?.message || 'Failed to fetch audit trail');
    } finally {
      setLoading(false);
    }
  };

  const getActionColor = (action) => {
    const colors = {
      'TRIP_CREATED': 'success',
      'SECTOR_CREATED': 'info',
      'SECTOR_ASSIGNED': 'warning',
      'CREW_RESPONSE': 'primary',
      'PILOT_UPSERTED': 'secondary',
      'USER_CREATED': 'success',
      'USER_UPDATED': 'warning',
      'USER_DELETED': 'error',
      'TRIP_UPDATED': 'info',
      'ATC_REQUESTED': 'warning',
      'ATC_SENT_TO_CREW': 'info',
      'ATC_ACK_SIGNED': 'success',
      'SECTOR_REQUIREMENTS_UPDATED': 'info'
    };
    return colors[action] || 'default';
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-GB', {
      timeZone: 'GMT',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }) + ' GMT';
  };

  const formatActor = (actor) => {
    if (!actor) return 'System';
    return `${actor.firstName} ${actor.lastName} (${actor.email})`;
  };

  const renderWorkflowDiagram = (workflowStatus) => {
    if (!workflowStatus) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Workflow Progress ({workflowStatus.progress}%)
          </Typography>
          
          <LinearProgress 
            variant="determinate" 
            value={workflowStatus.progress} 
            sx={{ mb: 2, height: 8, borderRadius: 4 }}
          />
          
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {workflowStatus.completedSteps} of {workflowStatus.totalSteps} steps completed
          </Typography>

          {/* ASCII Workflow Diagram */}
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, fontFamily: 'monospace', fontSize: '12px' }}>
            <Typography variant="subtitle2" gutterBottom>Workflow Diagram:</Typography>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
{workflowStatus.steps.map((step, index) => {
  const isLast = index === workflowStatus.steps.length - 1;
  const statusIcon = step.status === 'completed' ? '✓' : 
                    step.status === 'active' ? '▶' : '○';
  const statusColor = step.status === 'completed' ? '#4caf50' : 
                     step.status === 'active' ? '#ff9800' : '#9e9e9e';
  
  return `${statusIcon} ${step.name}${isLast ? '' : ' →'}`;
}).join('\n')}
            </pre>
          </Box>

          {workflowStatus.nextStep && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2">Next Step:</Typography>
              <Typography variant="body2">
                {workflowStatus.nextStep.name}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const handleLogClick = (log) => {
    setSelectedLog(log);
    setDetailDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading audit trail...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </Box>
    );
  }

  if (!auditData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No audit data available</Typography>
        <Button onClick={onClose} variant="outlined" sx={{ mt: 2 }}>
          Close
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">
          <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Audit Trail - {entityName}
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Workflow Status */}
      {renderWorkflowDiagram(auditData.workflowStatus)}

      {/* Charter/Sector Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {entityType === 'charter' ? 'Charter Details' : 'Sector Details'}
          </Typography>
          <Grid container spacing={2}>
            {entityType === 'charter' && auditData.charter && (
              <>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Charter Number
                  </Typography>
                  <Typography variant="body1">
                    {auditData.charter.charterNumber}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Title
                  </Typography>
                  <Typography variant="body1">
                    {auditData.charter.title}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={auditData.charter.status} 
                    color={auditData.charter.status === 'COMPLETED' ? 'success' : 'primary'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Sectors
                  </Typography>
                  <Typography variant="body1">
                    {auditData.sectors?.length || 0} sector(s)
                  </Typography>
                </Grid>
              </>
            )}
            {entityType === 'sector' && auditData.sector && (
              <>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Route
                  </Typography>
                  <Typography variant="body1">
                    {auditData.sector.departure?.airport} → {auditData.sector.arrival?.airport}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip 
                    label={auditData.sector.status} 
                    color={auditData.sector.status === 'COMPLETED' ? 'success' : 'primary'}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Departure
                  </Typography>
                  <Typography variant="body1">
                    {auditData.sector.departure?.date && new Date(auditData.sector.departure.date).toLocaleDateString()} {auditData.sector.departure?.time}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Arrival
                  </Typography>
                  <Typography variant="body1">
                    {auditData.sector.arrival?.date && new Date(auditData.sector.arrival.date).toLocaleDateString()} {auditData.sector.arrival?.time}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Audit Logs */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Activity Log ({auditData.auditLogs?.length || 0} entries)
          </Typography>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp (GMT)</TableCell>
                  <TableCell>Entity</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Actor</TableCell>
                  <TableCell>IP Address</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {auditData.auditLogs?.map((log) => (
                  <TableRow 
                    key={log._id} 
                    hover 
                    onClick={() => handleLogClick(log)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon sx={{ mr: 1, fontSize: 16 }} />
                        {formatDate(log.createdAt)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.entityType} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={log.action} 
                        color={getActionColor(log.action)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                        {formatActor(log.actorId)}
                      </Box>
                    </TableCell>
                    <TableCell>{log.ip || 'N/A'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog 
        open={detailDialogOpen} 
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Audit Log Details</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Entity Type
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.entityType}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Action
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.action}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Actor
                  </Typography>
                  <Typography variant="body1">
                    {formatActor(selectedLog.actorId)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Timestamp (GMT)
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(selectedLog.createdAt)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    IP Address
                  </Typography>
                  <Typography variant="body1">
                    {selectedLog.ip || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    User Agent
                  </Typography>
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                    {selectedLog.userAgent || 'N/A'}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              {/* Before/After Data */}
              {(selectedLog.before || selectedLog.after) && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Data Changes
                  </Typography>
                  
                  {selectedLog.before && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>Before</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ 
                          backgroundColor: '#f5f5f5', 
                          padding: '10px', 
                          borderRadius: '4px',
                          overflow: 'auto',
                          fontSize: '12px'
                        }}>
                          {JSON.stringify(selectedLog.before, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {selectedLog.after && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography>After</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <pre style={{ 
                          backgroundColor: '#f5f5f5', 
                          padding: '10px', 
                          borderRadius: '4px',
                          overflow: 'auto',
                          fontSize: '12px'
                        }}>
                          {JSON.stringify(selectedLog.after, null, 2)}
                        </pre>
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditTrail;
